<template>
  <div class="default-page" :data-horizontal-layout="isHorizontalLayout">
    <main class="main default-layout">
      <div class="headerreplacement" v-if="!page?.headerModule">
        <h1 class="_hidden" v-if="page?.title">{{ page.title }}</h1>
      </div>
      <component
        v-if="page?.headerModule"
        :key="page.headerModule.id"
        :is="HeaderModules[page.headerModule.__typename]"
        :data="page.headerModule"
      ></component>
      <component
        v-for="module in page?.contentModules"
        :key="module.id"
        :is="ContentModules[module.__typename]"
        :data="module"
      ></component>
      <!--  TODO: should be done via content module in dato -->
      <ContentCookieDeclaration v-if="page?.slug === 'impressum'" />
    </main>
    <SiteFooter theme="light" v-if="isHorizontalLayout === false" />
  </div>
</template>

<script setup lang="ts">
import { ContentModules } from '~/components/content-modules/ContentModules';
import { HeaderModules } from '~/components/header-modules/HeaderModules';
import gsap from 'gsap';
import type { ProjectFragment, GetPageBySlugQuery } from '#gql';
import { type SeoMetaTagType, toHead } from 'vue-datocms';
import ContentCookieDeclaration from '~/components/content-modules/ContentCookieDeclaration.vue';
import { buildPageStructuredDataJsonLd } from '~/utils/structured-data';
import {
  getLeafSlugFromPath,
  getPagePath,
  getRequestedPath,
  pagePathMatches,
  shouldRedirectFlatPageUrl
} from '~/utils/pageRouting';

const route = useRoute();
const pathSegments = computed(function () {
  const slugParam = route.params.slug;

  if (Array.isArray(slugParam)) {
    return slugParam.filter(Boolean);
  }

  return slugParam ? [slugParam] : [];
});
const requestedPath = computed(function () {
  return getRequestedPath(pathSegments.value);
});
const leafSlug = computed(function () {
  return getLeafSlugFromPath(pathSegments.value);
});

if (!leafSlug.value) {
  showError({
    statusCode: 404
  });
}

const { data, error } = await useAsyncGql({
  operation: 'GetPageBySlug',
  variables: { slug: leafSlug.value }
});

const page = (data.value as GetPageBySlugQuery).page;

if (!page || error.value) {
  showError({
    statusCode: 404
  });
}

if (shouldRedirectFlatPageUrl(page, pathSegments.value, requestedPath.value)) {
  await navigateTo(getPagePath(page), { redirectCode: 301 });
}

if (!pagePathMatches(page, requestedPath.value)) {
  showError({
    statusCode: 404
  });
}

const isHorizontalLayout = computed(function () {
  return false;
});

const floatingProjects = computed(function () {
  if (!page) {
    return null;
  } else if (!isHorizontalLayout.value) {
    return null;
  }

  return Array.from(page?.floatingProjects) as ProjectFragment[];
});

const floatingHeader = computed(function () {
  if (!page) {
    return null;
  } else if (!isHorizontalLayout.value) {
    return null;
  }

  return page.floatingHeader ?? '';
});

const floatingCta = computed(function () {
  if (!page) {
    return null;
  } else if (!isHorizontalLayout.value) {
    return null;
  }

  return page.floatingCta ?? '';
});

useHead(toHead(page?.seo as SeoMetaTagType[]));

const pageStructuredData = computed(() =>
  buildPageStructuredDataJsonLd({
    contentModules: page?.contentModules
  })
);

useHead(() => ({
  script: pageStructuredData.value
    ? [
      {
        key: 'page-team-structured-data',
        type: 'application/ld+json',
        innerHTML: JSON.stringify(pageStructuredData.value)
      }
    ]
    : []
}));

definePageMeta({
  pageTransition: {
    name: 'from-index',
    mode: 'out-in',
    onEnter: function (el, done) {
      if (el) {
        gsap.fromTo(
          el,
          {
            opacity: 0
          },
          {
            opacity: 1,
            duration: 1.25,
            ease: 'power2.in',
            onComplete: done
          }
        );
      } else {
        return done();
      }
    },
    onLeave: function (el, done) {
      const isHorizontalLayout = el.getAttribute('data-horizontal-layout') === 'true';

      const tl = gsap.timeline({
        onComplete: function () {
          done();
        }
      });

      if (!isHorizontalLayout) {
        tl.to(el, {
          opacity: 0
        });
      } else {
        const transitionElementsDown = document.querySelectorAll(
          "[data-transition='down']:not(.-in-transition)"
        );
        const transitionElementsUp = document.querySelectorAll(
          "[data-transition='up']:not(.-in-transition)"
        );

        if (transitionElementsDown && transitionElementsUp) {
          tl.to(transitionElementsDown, {
            y: window.innerHeight,
            duration: 0.5,
            ease: 'power4.in'
          }).to(
            transitionElementsUp,
            {
              y: window.innerHeight * -1,
              duration: 0.5,
              ease: 'power4.in'
            },
            '<'
          );
        }
      }
    }
  }
});
</script>

<style scoped lang="scss"></style>
